import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import * as api from "../api/client";
import { pixelTheme } from "../theme/pixel";
import { primaryBtn, withBtnStyle } from "../theme/styles";
import { inertAttrs } from "../logic/inert";

const MIN_PASSWORD = 10;

export function RegisterPage() {
  const theme = pixelTheme;
  const navigate = useNavigate();

  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (submitting) return;
    const name = username.trim();
    const mail = email.trim();
    if (!name || !mail || password.length < MIN_PASSWORD) return;
    setSubmitting(true);
    setError(null);
    try {
      await api.register(name, mail, password);
      // Account created but unverified — the user has to click the
      // link in the email before they can log in.
      navigate(`/check-your-email?email=${encodeURIComponent(mail)}`, {
        replace: true,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setSubmitting(false);
    }
  }

  const labelStyle: React.CSSProperties = {
    display: "grid",
    gap: 6,
    fontFamily: theme.fontDisplay,
    fontSize: 11,
    letterSpacing: theme.letterSpacing,
    color: theme.inkDim,
  };

  const inputStyle: React.CSSProperties = {
    fontFamily: theme.fontBody,
    fontSize: 20,
    padding: "8px 10px",
    background: theme.bgDeep,
    color: theme.ink,
    border: `2px solid ${theme.panelEdge}`,
  };
  const inputClass = "doi-input";

  return (
    <div
      style={{
        minHeight: "100%",
        background: theme.pageBg,
        color: theme.ink,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: theme.fontBody,
        padding: 20,
      }}
    >
      <form
        onSubmit={onSubmit}
        style={{
          background: theme.panel,
          border: `2px solid ${theme.panelEdgeStrong}`,
          padding: 28,
          width: "100%",
          maxWidth: 460,
          display: "grid",
          gap: 18,
        }}
      >
        <div
          style={{
            fontFamily: theme.fontDisplay,
            fontSize: 16,
            letterSpacing: theme.letterSpacing,
            color: theme.accent,
            textAlign: "center",
          }}
        >
          CREATE AN ACCOUNT
        </div>
        <label style={labelStyle}>
          USERNAME
          <input
            autoFocus
            type="text"
            autoComplete="username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            disabled={submitting}
            placeholder="pick a name"
            pattern="[A-Za-z0-9_\-]{3,32}"
            title="3–32 characters: letters, numbers, underscores, hyphens"
            style={inputStyle}
            className={inputClass}
          />
        </label>
        <label style={labelStyle}>
          EMAIL
          <input
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={submitting}
            placeholder="we'll send a verification link"
            style={inputStyle}
            className={inputClass}
          />
        </label>
        <label style={labelStyle}>
          PASSWORD
          <input
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={submitting}
            placeholder={`at least ${MIN_PASSWORD} characters`}
            style={inputStyle}
            className={inputClass}
          />
          <span style={{ fontSize: 11, color: theme.inkFaint }}>
            {password.length === 0
              ? `Minimum ${MIN_PASSWORD} characters. Pick anything you can remember.`
              : password.length < MIN_PASSWORD
                ? `${MIN_PASSWORD - password.length} more character${
                    MIN_PASSWORD - password.length === 1 ? "" : "s"
                  } to go.`
                : "Long enough."}
          </span>
        </label>
        {error && (
          <div style={{ color: theme.accent, fontSize: 14 }}>{error}</div>
        )}
        <button
          type="submit"
          {...inertAttrs(
            submitting ||
              username.trim().length < 3 ||
              email.trim().length === 0 ||
              password.length < MIN_PASSWORD,
          )}
          {...withBtnStyle(primaryBtn(theme, submitting), {
            fontSize: 12,
            padding: "10px 16px",
            cursor: submitting ? "wait" : undefined,
          })}
        >
          {submitting ? "…" : "CREATE ACCOUNT"}
        </button>
        <div style={{ textAlign: "center", fontSize: 12 }}>
          <Link to="/login" style={{ color: theme.inkDim }}>
            Already have an account? Log in.
          </Link>
        </div>
      </form>
    </div>
  );
}
